/**
 * WhatsApp through Meta's own Cloud API (ADR-105): the templates the platform
 * prepares on each store's account, what goes, and what the admin shows.
 */
/** The order updates WhatsApp can carry — each a Meta *utility* template. */
export const WHATSAPP_EVENTS = [
    'orderConfirmed',
    'orderShipped',
    'orderReadyForPickup',
    'orderDelivered',
    'orderCancelled',
    'orderRefunded',
];
/** The platform's cart reminders (ADR-106): Meta *marketing* templates, sent only with marketing consent. */
export const WHATSAPP_CART_MESSAGES = ['cartReminder', 'cartOffer'];
/** Each update as a person names it — the log's and the timeline's words, never the template's code. */
export const WHATSAPP_EVENT_NAMES = {
    cartReminder: { ar: 'واتساب: تذكير بالسلة', en: 'WhatsApp: cart reminder' },
    cartOffer: { ar: 'واتساب: تذكير بالسلة مع كود', en: 'WhatsApp: cart reminder with a code' },
    supportFollowUp: { ar: 'واتساب: ردّ الدعم بانتظارك', en: 'WhatsApp: a support reply is waiting' },
    orderConfirmed: { ar: 'واتساب: تأكيد الطلب', en: 'WhatsApp: order confirmed' },
    orderShipped: { ar: 'واتساب: الطلب في الطريق', en: 'WhatsApp: order on its way' },
    orderReadyForPickup: { ar: 'واتساب: جاهز للاستلام', en: 'WhatsApp: ready to collect' },
    orderDelivered: { ar: 'واتساب: تم التسليم', en: 'WhatsApp: order delivered' },
    orderCancelled: { ar: 'واتساب: أُلغي الطلب', en: 'WhatsApp: order cancelled' },
    orderRefunded: { ar: 'واتساب: استرداد مبلغ', en: 'WhatsApp: refund' },
};
export const WHATSAPP_DEFAULTS = {
    events: {
        orderConfirmed: true,
        orderShipped: true,
        orderReadyForPickup: true,
        orderDelivered: true,
        orderCancelled: true,
        orderRefunded: true,
    },
    language: 'ar',
    cartReminders: false,
    inbox: true,
};
/** The Graph API version used unless the store's integration names another. */
export const WHATSAPP_GRAPH_VERSION = 'v25.0';
export const WHATSAPP_TEMPLATES = [
    {
        event: 'orderConfirmed',
        name: 'ecom_order_confirmed_1',
        category: 'UTILITY',
        params: ['customer.name', 'order.number', 'order.total'],
        body: {
            ar: 'مرحبًا {{1}}، شكرًا لطلبك. رقم طلبك {{2}} وإجماليه {{3}}. سنرسل لك هنا تحديثًا حين يُشحن.',
            en: 'Hello {{1}}, thank you for your order. Your order number is {{2}} and its total is {{3}}. We will update you here when it ships.',
        },
        example: { ar: ['سارة', 'ORD-2026-000123', '287.50 ر.س'], en: ['Sara', 'ORD-2026-000123', 'SAR 287.50'] },
    },
    {
        event: 'orderShipped',
        name: 'ecom_order_shipped_1',
        category: 'UTILITY',
        params: ['customer.name', 'order.number', 'shipping.carrier', 'shipping.tracking'],
        body: {
            ar: 'مرحبًا {{1}}، طلبك {{2}} في الطريق إليك مع {{3}}. للتتبع: {{4}} — نتمنى أن ينال إعجابك.',
            en: 'Hello {{1}}, your order {{2}} is on its way with {{3}}. To track it: {{4}} — we hope you love it.',
        },
        example: {
            ar: ['سارة', 'ORD-2026-000123', 'سمسا', 'https://example.com/track/SM123'],
            en: ['Sara', 'ORD-2026-000123', 'SMSA', 'https://example.com/track/SM123'],
        },
    },
    {
        event: 'orderReadyForPickup',
        name: 'ecom_order_ready_for_pickup_1',
        category: 'UTILITY',
        params: ['customer.name', 'order.number'],
        body: {
            ar: 'مرحبًا {{1}}، طلبك {{2}} جاهز للاستلام. أحضر رقم الطلب معك عند الاستلام من فضلك.',
            en: 'Hello {{1}}, your order {{2}} is ready to collect. Please bring the order number with you.',
        },
        example: { ar: ['سارة', 'ORD-2026-000123'], en: ['Sara', 'ORD-2026-000123'] },
    },
    {
        event: 'orderDelivered',
        name: 'ecom_order_delivered_1',
        category: 'UTILITY',
        params: ['customer.name', 'order.number'],
        body: {
            ar: 'مرحبًا {{1}}، تم تسليم طلبك {{2}}. شكرًا لتسوقك معنا، ويسعدنا أن نسمع رأيك.',
            en: 'Hello {{1}}, your order {{2}} has been delivered. Thank you for shopping with us — we would love to hear what you think.',
        },
        example: { ar: ['سارة', 'ORD-2026-000123'], en: ['Sara', 'ORD-2026-000123'] },
    },
    {
        event: 'orderCancelled',
        name: 'ecom_order_cancelled_1',
        category: 'UTILITY',
        params: ['customer.name', 'order.number'],
        body: {
            ar: 'مرحبًا {{1}}، أُلغي طلبك {{2}}. إن كان لديك سؤال فرُدّ على هذه الرسالة أو تواصل معنا.',
            en: 'Hello {{1}}, your order {{2}} has been cancelled. If you have a question, reply to this message or contact us.',
        },
        example: { ar: ['سارة', 'ORD-2026-000123'], en: ['Sara', 'ORD-2026-000123'] },
    },
    {
        event: 'orderRefunded',
        name: 'ecom_order_refunded_1',
        category: 'UTILITY',
        params: ['customer.name', 'refund.amount', 'order.number'],
        body: {
            ar: 'مرحبًا {{1}}، أعدنا مبلغ {{2}} عن طلبك {{3}}. قد يستغرق ظهوره في حسابك بضعة أيام.',
            en: 'Hello {{1}}, we have refunded {{2}} for your order {{3}}. It may take a few days to show in your account.',
        },
        example: { ar: ['سارة', '287.50 ر.س', 'ORD-2026-000123'], en: ['Sara', 'SAR 287.50', 'ORD-2026-000123'] },
    },
    {
        event: 'cartReminder',
        name: 'ecom_cart_reminder_1',
        category: 'MARKETING',
        params: ['customer.name', 'cart.items', 'cart.url'],
        body: {
            ar: 'مرحبًا {{1}}، ما زالت سلتك محفوظة لدينا وفيها {{2}}. أكمل طلبك من هنا: {{3}} — ويسعدنا مساعدتك إن احتجت أي شيء.',
            en: 'Hello {{1}}, your basket is still saved with {{2}} in it. Complete your order here: {{3}} — we are happy to help if you need anything.',
        },
        example: {
            ar: ['سارة', 'محفظة جلدية وميدالية', 'https://example.com/cart'],
            en: ['Sara', 'a leather wallet and a keyring', 'https://example.com/cart'],
        },
    },
    {
        event: 'cartOffer',
        name: 'ecom_cart_offer_1',
        category: 'MARKETING',
        params: ['customer.name', 'offer.code', 'offer.percent', 'cart.url'],
        body: {
            ar: 'مرحبًا {{1}}، ما زالت سلتك محفوظة لدينا. استخدم الكود {{2}} لخصم {{3}} على طلبك، وأكمله من هنا: {{4}} — العرض لفترة محدودة.',
            en: 'Hello {{1}}, your basket is still saved. Use the code {{2}} for {{3}} off your order and complete it here: {{4}} — the offer is for a limited time.',
        },
        example: {
            ar: ['سارة', 'BACK-7K2Q', '10%', 'https://example.com/cart'],
            en: ['Sara', 'BACK-7K2Q', '10%', 'https://example.com/cart'],
        },
    },
    {
        // Support (ADR-107): after the 24-hour window, the one thing a business may say is that an answer waits.
        event: 'supportFollowUp',
        name: 'ecom_support_followup_1',
        category: 'UTILITY',
        params: ['customer.name', 'ticket.number'],
        body: {
            ar: 'مرحبًا {{1}}، لدينا ردّ على محادثتك {{2}} معنا. أرسل أي رسالة هنا وستصلك الإجابة فورًا.',
            en: 'Hello {{1}}, we have a reply to your conversation {{2}} with us. Send any message here and you will get it straight away.',
        },
        example: { ar: ['سارة', 'TKT-2026-000042'], en: ['Sara', 'TKT-2026-000042'] },
    },
];
/** Meta's verdict on a template, or that it was never sent for review. */
export const WHATSAPP_TEMPLATE_STATUSES = ['NOT_SUBMITTED', 'PENDING', 'APPROVED', 'REJECTED', 'PAUSED', 'DISABLED'];
//# sourceMappingURL=whatsapp.js.map